import { type FC, type KeyboardEvent, type MouseEvent, useCallback, useEffect } from "react";
import { cn } from "../../../utils";
import { FieldCheckbox, type FieldCheckboxPropsMultiple } from "../field-checkbox";
import { type GridRowData, useGridForm } from "./use-grid-form";

const GRID_ROW_CHECKBOX_DATA_ATTR = "data-grid-row-checkbox-id";

export interface GridRowCheckboxProps extends Partial<FieldCheckboxPropsMultiple> {
  row: GridRowData;
}

export const GridRowCheckbox: FC<GridRowCheckboxProps> = ({ row, className, ...props }) => {
  const { getLastClickedRowId, setLastClickedRowId, selectedRowIds, formContext } = useGridForm();
  const { setValue, getValues } = formContext;

  useEffect(
    () => () => {
      // Right now, this works, because we don't have a use case for hiding/showing rows
      // while keeping them selected. If we every do need to hide/show rows while maintaining
      // the selection, we will need to change this.
      // One idea would be to have a way to hide/show a row using a `hidden` prop on the row.
      // Then we could reference the grid `data` and see if the row still exists.
      setValue(
        `rowIds`,
        getValues("rowIds").filter((id: string) => id !== row.id)
      );
    },
    [setValue, getValues, row.id]
  );

  // IMPORTANT: We use `onClickCapture` instead of `onClick` because the Radix Checkbox
  // dispatches a synthetic click event when the checkbox state changes.
  // That synthetic event doesn't include keyboard modifier state (shiftKey, etc.),
  // so we capture the real user interaction via click capture, which fires first.
  const handleClickCapture = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      // Avoid handling the synthetic click event dispatched by the Radix Checkbox
      // on the checkbox "INPUT" element  when the checkbox state changes when the
      // checkbox state changes, as it will result in this function being called twice
      // and will undermine the logic for determining the last clicked row id.
      if ((event.target as HTMLElement).tagName !== "BUTTON") {
        return;
      }

      event.stopPropagation();

      // Prevent the Radix Checkbox from processing the click and triggering
      // onCheckedChange, which would double-toggle the selection state.
      event.preventDefault();

      const currentRowId = row.id;
      const lastClickedRowId = getLastClickedRowId();

      // Create a set of the selected row ids.
      const rowIdsSet = new Set(selectedRowIds);

      // Check if the current row is checked.
      const isChecked = rowIdsSet.has(currentRowId);

      // Always update the last clicked row id.
      setLastClickedRowId(currentRowId);

      // Since we stop propagation of the click event, we need to handle the
      // case where the user clicks on the checkbox without holding shift.
      if (!event.shiftKey || !lastClickedRowId) {
        if (isChecked) {
          rowIdsSet.delete(currentRowId);
        } else {
          rowIdsSet.add(currentRowId);
        }
        setValue("rowIds", [...rowIdsSet]);
        return;
      }

      // Get the elements for the current and last clicked rows.
      const currentRowElement = document.querySelector(`[${GRID_ROW_CHECKBOX_DATA_ATTR}="${currentRowId}"]`);
      const lastClickedRowElement = document.querySelector(`[${GRID_ROW_CHECKBOX_DATA_ATTR}="${lastClickedRowId}"]`);

      if (!currentRowElement || !lastClickedRowElement) {
        return;
      }

      // Get all the checkbox elements in the form.
      const allCheckboxElements = currentRowElement
        ?.closest("form")
        ?.querySelectorAll(`[${GRID_ROW_CHECKBOX_DATA_ATTR}]`);

      // Get the indices of the current and last clicked rows.
      const currentRowIndex = [...(allCheckboxElements ?? [])].indexOf(currentRowElement as unknown as Element);
      const lastClickedRowIndex = [...(allCheckboxElements ?? [])].indexOf(lastClickedRowElement as unknown as Element);

      // Determine the start and end indices of the rows to toggle.
      const startIndex = Math.min(currentRowIndex, lastClickedRowIndex);
      const endIndex = Math.max(currentRowIndex, lastClickedRowIndex) + 1;

      // Get the elements from the current row index to the last clicked row index.
      const rowsToToggle = [...(allCheckboxElements ?? [])].slice(startIndex, endIndex);

      // Get the ids for each of the intermediate rows from their data attributes.
      const rowIdsToToggle = rowsToToggle.map((row) => row.getAttribute(GRID_ROW_CHECKBOX_DATA_ATTR)) as string[];

      if (!rowIdsToToggle?.length) {
        return;
      }

      if (!isChecked) {
        rowIdsToToggle.forEach((id) => rowIdsSet.add(id));
      } else {
        rowIdsToToggle.forEach((id) => rowIdsSet.delete(id));
      }

      // Update the selected row ids.
      setValue("rowIds", [...rowIdsSet]);
    },
    [row.id, selectedRowIds, getLastClickedRowId, setLastClickedRowId, setValue]
  );

  const handleKeyUp = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.code === "Space") {
      event.stopPropagation();
    }

    props.inputProps?.onKeyUpCapture?.(event);
  };

  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  return (
    // NOTE: We need to wrap the checkbox in a div so that the click event is not propagated to the parent.
    // This is because the checkbox is emitting a click event whenever the checked value changes.
    // We don't want the click event to propagate to the parent, because the checkbox may be inside a row that is a clickable area.
    // So we need to wrap the checkbox in a div and prevent the click event from propagating to the parent.
    // This is a workaround to avoid the issue.
    // SEE: https://github.com/radix-ui/primitives/blob/36d954d3c1b41c96b1d2e875b93fc9362c8c09e6/packages/react/checkbox/src/checkbox.tsx#L337
    <div onClick={handleClick} onClickCapture={handleClickCapture} {...{ [GRID_ROW_CHECKBOX_DATA_ATTR]: row.id }}>
      <FieldCheckbox
        {...props}
        name="rowIds"
        value={row.id}
        multiple
        inputProps={{
          ...props.inputProps,
          onKeyUpCapture: handleKeyUp,
          className: cn("text-[10px]", props.inputProps?.className),
        }}
        className={cn("flex-none", className)}
      />
    </div>
  );
};
